import { ReparoApi } from '@api';
import {
    type AgentMessage,
    type AIMessageContext,
    type ApiError,
    type GetUserFeedbackResponseDto,
    type SuggestedPrompt,
    type UserMessageFeedback,
    type UserThreadList,
    type UserThreadMapping,
} from '@diagon-alley/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useToaster from '@hooks/toaster/useToaster';
import { useTranslate } from '@hooks/i18n/useTranslate';

// Query keys for React Query
const QueryKeys = {
    GET_AI_ANALYST_PROMPTS: 'get-ai-analyst-prompts',
    CREATE_AI_ANALYST_THREAD: 'create-ai-analyst-thread',
    GET_AI_CHAT_THREADS: 'get-ai-chat-threads',
    GET_AI_CHAT_THREAD: 'get-ai-chat-thread',
    CREATE_AI_ANALYST_FEEDBACK: 'create-ai-analyst-feedback',
} as const;

// Get suggested prompts (skeleton - endpoint may not exist yet)
const getAiAnalystPrompts = async (
    context: AIMessageContext,
): Promise<SuggestedPrompt[]> => {
    // TODO: Implement when backend endpoint is available
    // return ReparoApi<SuggestedPrompt[]>({
    //     url: `/ai/suggested-prompts?context=${context}`,
    //     method: 'GET',
    // });
    return [];
};

interface UseAiAnalystPromptsProps {
    context: AIMessageContext;
}

export const useAiAnalystPrompts = ({ context }: UseAiAnalystPromptsProps) => {
    const prompts = useQuery<SuggestedPrompt[], ApiError>({
        queryKey: [QueryKeys.GET_AI_ANALYST_PROMPTS, context],
        queryFn: () => getAiAnalystPrompts(context),
        enabled: true,
    });
    return prompts;
};

// Create a new thread (skeleton - endpoint may not exist yet)
const createAiAnalystThread = async (): Promise<UserThreadMapping> => {
    // TODO: Implement when backend endpoint is available
    // For now, create a local thread ID
    const threadId = `thread_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return {
        threadId,
        userUuid: '', // Will be filled by backend
        createdAt: new Date(),
        threadName: 'New chat',
        updatedAt: new Date(),
    };
};

export const useAiAnalystThread = () => {
    const queryClient = useQueryClient();
    const response = useMutation<UserThreadMapping, ApiError>({
        mutationFn: () => createAiAnalystThread(),
        mutationKey: [QueryKeys.CREATE_AI_ANALYST_THREAD],
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [QueryKeys.GET_AI_CHAT_THREADS],
            });
        },
    });

    return response;
};

// Update feedback (skeleton - endpoint may not exist yet)
const updateAiAnalystFeedback = async (
    payload: Pick<
        UserMessageFeedback,
        'threadId' | 'messageId' | 'sentiment' | 'metadata' | 'traceId'
    >,
): Promise<GetUserFeedbackResponseDto> => {
    // TODO: Implement when backend endpoint is available
    // return ReparoApi<GetUserFeedbackResponseDto>({
    //     url: `/ai/user-message-feedback`,
    //     method: 'PUT',
    //     body: JSON.stringify(payload),
    // });
    return {
        status: 'ok',
        results: {
            ...payload,
            userUuid: '',
            createdAt: new Date(),
        },
    };
};

export const useAiAnalystFeedback = () => {
    const response = useMutation<
        GetUserFeedbackResponseDto,
        ApiError,
        Pick<
            UserMessageFeedback,
            'threadId' | 'messageId' | 'sentiment' | 'metadata' | 'traceId'
        >
    >({
        mutationFn: (payload) => updateAiAnalystFeedback(payload),
        mutationKey: [QueryKeys.CREATE_AI_ANALYST_FEEDBACK],
    });
    return response;
};

// Get chat threads (skeleton - endpoint may not exist yet)
const getAiChatThreads = async (
    perPage: number,
    currentPage: number,
    threadName?: string,
): Promise<UserThreadList> => {
    // TODO: Implement when backend endpoint is available
    // return ReparoApi<UserThreadList>({
    //     url: `/ai/threads?perPage=${perPage}&currentPage=${currentPage}${
    //         threadName ? `&threadName=${threadName}` : ''
    //     }`,
    //     method: 'GET',
    // });
    return {
        data: [],
        paginate: {
            currentPage,
            perPage,
            from: 0,
            to: 0,
        },
    };
};

export const useAiChatThreads = ({
    perPage,
    currentPage,
    threadName,
}: {
    perPage: number;
    currentPage: number;
    threadName?: string;
}) => {
    const response = useQuery<UserThreadList, ApiError>({
        queryKey: [
            QueryKeys.GET_AI_CHAT_THREADS,
            perPage,
            currentPage,
            threadName,
        ],
        queryFn: () => getAiChatThreads(perPage, currentPage, threadName),
        enabled: !!perPage && !!currentPage,
    });
    return response;
};

// Get chat messages for a thread
export const getAiChatMessages = async (
    threadId: string,
): Promise<AgentMessage[]> =>
    ReparoApi({
        url: `/ai/threads/${threadId}/messages`,
        method: 'GET',
        body: undefined,
    }) as unknown as Promise<AgentMessage[]>;

// Get a specific thread
export const getAiChatThread = async (
    threadId: string,
): Promise<UserThreadMapping> =>
    ReparoApi({
        url: `/ai/threads/${threadId}`,
        method: 'GET',
        body: undefined,
    }) as unknown as Promise<UserThreadMapping>;

export const useGetAiChatThread = (threadId: string) => {
    const response = useQuery<UserThreadMapping, ApiError>({
        queryKey: [QueryKeys.GET_AI_CHAT_THREAD, threadId],
        queryFn: () => getAiChatThread(threadId),
        enabled: !!threadId,
    });
    return response;
};

// Delete a thread (skeleton - endpoint may not exist yet)
const deleteUserThread = async (threadId: string): Promise<void> => {
    // TODO: Implement when backend endpoint is available
    // return ReparoApi<void>({
    //     url: `/ai/user-thread/${threadId}`,
    //     method: 'DELETE',
    // });
    return Promise.resolve();
};

export const useDeleteUserThread = () => {
    const queryClient = useQueryClient();
    const { showToastSuccess, showToastError } = useToaster();
    const { t } = useTranslate();

    const response = useMutation<void, ApiError, string>({
        mutationFn: (threadId: string) => deleteUserThread(threadId),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [QueryKeys.GET_AI_CHAT_THREADS],
            });
            showToastSuccess({
                title: t('Chat deleted successfully'),
            });
        },
        onError: () => {
            showToastError({
                title: t('Failed to delete chat'),
            });
        },
    });
    return response;
};

// Update thread (skeleton - endpoint may not exist yet)
const updateAiAnalystThread = async (
    threadId: string,
    payload: Partial<Pick<UserThreadMapping, 'threadName'>>,
): Promise<UserThreadMapping> => {
    // TODO: Implement when backend endpoint is available
    // return ReparoApi<UserThreadMapping>({
    //     url: `/ai/user-thread/${threadId}`,
    //     method: 'PATCH',
    //     body: JSON.stringify(payload),
    // });
    return {
        threadId,
        userUuid: '',
        createdAt: new Date(),
        threadName: payload.threadName || 'New chat',
        updatedAt: new Date(),
    };
};

type UpdateThreadVariables = {
    threadId: string;
    payload: Partial<Pick<UserThreadMapping, 'threadName'>>;
};

export const useUpdateAiAnalystThread = () => {
    const queryClient = useQueryClient();

    const response = useMutation<
        UserThreadMapping,
        ApiError,
        UpdateThreadVariables
    >({
        mutationFn: ({ threadId, payload }) =>
            updateAiAnalystThread(threadId, payload),
        onSuccess: async (data) => {
            await queryClient.invalidateQueries({
                queryKey: [QueryKeys.GET_AI_CHAT_THREADS],
            });
            await queryClient.invalidateQueries({
                queryKey: [QueryKeys.GET_AI_CHAT_THREAD, data.threadId],
            });
        },
    });
    return response;
};

